import React, { useState, useMemo } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Paper,
  Button,
  Collapse,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Avatar,
  Stack,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Edit,
  Delete,
  Today,
  Restore,
  DeleteOutline,
  CheckCircle,
  Payment,
  PersonOff,
  Schedule,
  Warning,
  History,
  ExpandMore,
  ExpandLess,
  PlayArrow,
  Event,
  Repeat,
  Search,
  ViewList,
  ViewTimeline,
  ViewKanban,
  Add,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';

interface Reservation {
  id: string;
  clientId?: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  startDate: string;
  endDate: string;
  status: string;
  statusHistory?: any[];
  paymentStatus?: 'pending' | 'partial' | 'paid' | 'refunded';
  completionStatus?: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'no_show' | 'cancelled';
  rescheduleCount?: number;
  lastRescheduleDate?: string;
  notes?: string;
}

interface ReservationsSectionProps {
  reservations: Reservation[];
  deletedReservations?: Reservation[];
  onEdit: (reservation: Reservation) => void;
  onDelete: (id: string) => Promise<void>;
  onRestore?: (id: string) => Promise<void>;
  onUpdateStatus?: (reservationId: string, updates: Partial<Reservation>) => Promise<void>;
  onOpenShiftStatus?: (reservation: Reservation) => void;
  recurringTemplates?: any[];
  onCreateReservation?: () => void;
}

type ViewMode = 'list' | 'timeline' | 'kanban';

const STATUS_CONFIG: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  scheduled: { label: 'Programado', color: 'default' },
  confirmed: { label: 'Confirmado', color: 'success' },
  in_progress: { label: 'En Curso', color: 'warning' },
  completed: { label: 'Completado', color: 'success' },
  no_show: { label: 'No Asistió', color: 'error' },
  cancelled: { label: 'Cancelado', color: 'error' },
  pending: { label: 'Pendiente', color: 'warning' },
};

const PAYMENT_CONFIG: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  pending: { label: 'Pendiente', color: 'warning' },
  partial: { label: 'Parcial', color: 'warning' },
  paid: { label: 'Pagado', color: 'success' },
  refunded: { label: 'Reembolsado', color: 'info' },
};

interface CompactReservationCardProps {
  reservation: Reservation;
  onEdit?: (reservation: Reservation) => void;
  onDelete?: (id: string) => void;
  onUpdateStatus?: (reservationId: string, updates: Partial<Reservation>) => Promise<void>;
  onOpenShiftStatus?: (reservation: Reservation) => void;
}

const CompactReservationCard: React.FC<CompactReservationCardProps> = ({ reservation, onEdit, onDelete, onUpdateStatus, onOpenShiftStatus }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const statusConfig = STATUS_CONFIG[reservation.completionStatus || reservation.status] || { label: reservation.status, color: 'default' as const };
    const paymentConfig = reservation.paymentStatus ? (PAYMENT_CONFIG[reservation.paymentStatus] || { label: reservation.paymentStatus, color: 'default' as const }) : null;

    const formatTime = (dateStr: string) => {
        return new Date(dateStr).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    };

    const isOverdue = reservation.status === 'pending' && new Date(reservation.startDate) < new Date();
    const isInProgress = (reservation.completionStatus === 'in_progress') || (new Date(reservation.startDate) <= new Date() && new Date(reservation.endDate) >= new Date() && reservation.status !== 'cancelled' && reservation.status !== 'completed');

    return (
        <Paper elevation={0} sx={{
            p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 4,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            borderLeft: isOverdue ? '6px solid #ef4444' : isInProgress ? '6px solid #10b981' : '6px solid transparent',
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': { 
                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.02)',
                transform: 'translateY(-2px)',
                boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.4)' : '0 10px 30px rgba(0,0,0,0.05)'
            }
        }}>
            <Box sx={{ 
                minWidth: 90, 
                textAlign: 'center', 
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', 
                borderRadius: 3, 
                p: 1.5,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
            }}>
                <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main', display: 'block', mb: 0.5 }}>
                    {new Date(reservation.startDate).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1, color: isDark ? 'white' : 'inherit' }}>
                    {formatTime(reservation.startDate)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{formatTime(reservation.endDate)}</Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ display: 'flex' }}>
                    <Avatar sx={{ 
                        width: 44, height: 44, 
                        bgcolor: isDark ? 'primary.dark' : 'primary.light', 
                        color: isDark ? 'white' : 'primary.main',
                        fontWeight: 900,
                        fontSize: 16
                    }}>
                        {reservation.customerName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 800, lineHeight: 1.2, mb: 0.5 }}>
                            {reservation.customerName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                {reservation.customerPhone || reservation.customerEmail}
                            </Typography>
                            {reservation.notes && (
                                <Tooltip title={reservation.notes}>
                                    <Chip label="Con Notas" size="small" variant="outlined" sx={{ height: 16, fontSize: '0.65rem' }} />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Stack>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
                <Chip 
                    label={statusConfig.label} 
                    color={statusConfig.color} 
                    size="small" 
                    sx={{ fontWeight: 900, borderRadius: 2, px: 1 }} 
                />
                {paymentConfig && (
                    <Chip 
                        label={paymentConfig.label} 
                        color={paymentConfig.color} 
                        variant="outlined"
                        size="small" 
                        sx={{ fontWeight: 700, borderRadius: 2, px: 1 }} 
                    />
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
                {onUpdateStatus && reservation.status !== 'cancelled' && reservation.status !== 'completed' && (
                    <>
                        <Tooltip title="Completar Turno">
                            <IconButton size="small" onClick={() => onUpdateStatus(reservation.id, { completionStatus: 'completed', status: 'completed' })} sx={{ color: 'success.main', bgcolor: 'success.light', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                                <CheckCircle fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Registrar Pago">
                            <IconButton size="small" onClick={() => onUpdateStatus(reservation.id, { paymentStatus: 'paid' })} sx={{ color: 'info.main', bgcolor: 'info.light', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                                <Payment fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                {onOpenShiftStatus && (
                    <Tooltip title="Gestionar Detalle">
                        <IconButton size="small" onClick={() => onOpenShiftStatus(reservation)} sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', color: 'secondary.main' }}>
                            <Schedule fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Más opciones">
                    <IconButton size="small" onClick={() => onEdit?.(reservation)} sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
                        <Edit fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    );
};

const TimelineView: React.FC<{
  reservations: Reservation[];
  onEdit?: (reservation: Reservation) => void;
}> = ({ reservations, onEdit }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hours = Array.from({ length: 15 }, (_, i) => i + 7); // 7 AM to 9 PM
  const HOUR_HEIGHT = 100;

  return (
    <Box sx={{ overflowX: 'auto', bgcolor: isDark ? 'rgba(0,0,0,0.2)' : '#f8fafc', borderRadius: 5, p: 3 }}>
      <Box sx={{ minWidth: 800, minHeight: hours.length * HOUR_HEIGHT + 100, position: 'relative' }}>
        <Box sx={{ display: 'flex', borderBottom: '2px solid', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', pb: 2, mb: 2 }}>
          <Box sx={{ width: 80 }} />
          {hours.map(hour => (
            <Box key={hour} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: 'text.secondary' }}>{hour}:00</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ position: 'relative', height: hours.length * HOUR_HEIGHT }}>
          {hours.map((hour, idx) => (
            <Box key={hour} sx={{ 
                position: 'absolute', left: 0, right: 0, 
                top: idx * HOUR_HEIGHT, 
                height: HOUR_HEIGHT, 
                borderTop: idx === 0 ? 'none' : '1px dashed', 
                borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                display: 'flex', alignItems: 'flex-start'
            }}>
                <Typography variant="caption" sx={{ width: 80, mt: -1, fontWeight: 700, color: 'text.secondary' }}>{hour}:00</Typography>
            </Box>
          ))}
          {reservations.map(reservation => {
            const startTime = new Date(reservation.startDate);
            const endTime = new Date(reservation.endDate);
            const startHours = startTime.getHours() + startTime.getMinutes() / 60;
            const endHours = endTime.getHours() + endTime.getMinutes() / 60;
            const top = (startHours - 7) * HOUR_HEIGHT;
            const height = Math.max((endHours - startHours) * HOUR_HEIGHT, 30);
            if (top < 0 || top > hours.length * HOUR_HEIGHT) return null;
            const statusConfig = STATUS_CONFIG[reservation.completionStatus || reservation.status] || { label: reservation.status, color: 'default' as const };
            return (
              <Box key={reservation.id} sx={{
                position: 'absolute', left: 100 + ((parseInt(String(reservation.id).slice(-2), 16) % 5) * 20),
                top, height, minWidth: 150, maxWidth: 250,
                bgcolor: isDark ? 'rgba(99, 102, 241, 0.15)' : 'white',
                border: `1px solid ${isDark ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.2)'}`,
                borderLeft: `4px solid ${statusConfig.color === 'success' ? '#10b981' : statusConfig.color === 'warning' ? '#f59e0b' : '#6366f1'}`,
                borderRadius: 2, p: 1.5, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s', zIndex: 1, boxShadow: 1,
                '&:hover': { transform: 'scale(1.02) translateX(5px)', zIndex: 10, boxShadow: 4, bgcolor: isDark ? 'rgba(99, 102, 241, 0.25)' : '#fff' }
              }} onClick={() => onEdit?.(reservation)}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.1, mb: 0.5 }} noWrap>{reservation.customerName}</Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700, color: 'text.secondary' }} noWrap>
                  <Schedule sx={{ fontSize: 12 }} />
                  {startTime.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} - {endTime.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

interface KanbanViewProps {
  reservations: Reservation[];
  onEdit?: (reservation: Reservation) => void;
  onUpdateStatus?: (reservationId: string, updates: Partial<Reservation>) => Promise<void>;
  onDelete?: (id: string) => void;
}

const KanbanView: React.FC<KanbanViewProps> = ({ reservations, onEdit, onUpdateStatus, onDelete }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const columns = [
        { id: 'pending', label: 'Pendientes', color: '#f59e0b', icon: <Schedule /> },
        { id: 'confirmed', label: 'Confirmados', color: '#6366f1', icon: <CheckCircle /> },
        { id: 'in_progress', label: 'En Curso', color: '#10b981', icon: <PlayArrow /> },
        { id: 'completed', label: 'Completados', color: '#64748b', icon: <History /> },
    ];

    const getColumnReservations = (statusId: string) => {
        if (statusId === 'pending') return reservations.filter(r => r.status === 'pending' || r.completionStatus === 'scheduled');
        if (statusId === 'in_progress') return reservations.filter(r => r.completionStatus === 'in_progress' || (r.status === 'confirmed' && new Date(r.startDate) <= new Date() && new Date(r.endDate) >= new Date()));
        if (statusId === 'confirmed') return reservations.filter(r => r.status === 'confirmed' && r.completionStatus !== 'in_progress' && new Date(r.startDate) > new Date());
        return reservations.filter(r => r.completionStatus === statusId || r.status === statusId);
    };

    return (
        <Box sx={{ display: 'flex', gap: 3, overflowX: 'auto', pb: 4, minHeight: 600, pt: 1 }}>
            {columns.map(column => {
                const columnReservations = getColumnReservations(column.id);
                return (
                    <Box key={column.id} sx={{ 
                        minWidth: 300, 
                        flex: 1, 
                        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)', 
                        borderRadius: 5, 
                        p: 2, 
                        display: 'flex', 
                        flexDirection: 'column',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: column.color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {column.icon}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1rem' }}>{column.label}</Typography>
                            <Chip 
                                label={columnReservations.length} 
                                size="small" 
                                sx={{ ml: 'auto', fontWeight: 900, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }} 
                            />
                        </Box>
                        
                        <Stack spacing={2} sx={{ flex: 1, overflowY: 'auto' }}>
                            {columnReservations.map(reservation => (
                                <Paper 
                                    key={reservation.id} 
                                    elevation={0} 
                                    onClick={() => onEdit?.(reservation)}
                                    sx={{ 
                                        p: 2, 
                                        borderRadius: 4, 
                                        cursor: 'pointer', 
                                        bgcolor: isDark ? 'rgba(30,30,30,0.8)' : 'white',
                                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                                        transition: 'all 0.2s',
                                        '&:hover': { transform: 'translateY(-3px)', boxShadow: 2, borderColor: column.color }
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>{reservation.customerName}</Typography>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                        <Schedule fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                                            <Box component="span" sx={{ color: 'primary.main', fontWeight: 900 }}>
                                                {new Date(reservation.startDate).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}
                                            </Box>
                                            •
                                            <Box component="span">
                                                {new Date(reservation.startDate).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs
                                            </Box>
                                        </Typography>
                                    </Stack>
                                    
                                    <Divider sx={{ mb: 1.5, opacity: 0.5 }} />
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Avatar sx={{ width: 24, height: 24, fontSize: 12, fontWeight: 800 }}>{reservation.customerName.charAt(0)}</Avatar>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {onUpdateStatus && column.id === 'pending' && (
                                                <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onUpdateStatus(reservation.id, { status: 'confirmed' }); }}>
                                                    <CheckCircle fontSize="small" />
                                                </IconButton>
                                            )}
                                            {onUpdateStatus && column.id === 'confirmed' && (
                                                <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); onUpdateStatus(reservation.id, { completionStatus: 'in_progress' }); }}>
                                                    <PlayArrow fontSize="small" />
                                                </IconButton>
                                            )}
                                            {onUpdateStatus && column.id === 'in_progress' && (
                                                <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); onUpdateStatus(reservation.id, { completionStatus: 'completed', status: 'completed' }); }}>
                                                    <CheckCircle fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            ))}
                            {columnReservations.length === 0 && (
                                <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.01)', borderRadius: 3, border: '1px dashed rgba(0,0,0,0.1)' }}>
                                    <Typography variant="caption" color="text.secondary">Vacío</Typography>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                );
            })}
        </Box>
    );
};

const ReservationsSection: React.FC<ReservationsSectionProps> = ({
  reservations, deletedReservations = [], onEdit, onDelete, onRestore, onUpdateStatus, onOpenShiftStatus, recurringTemplates = [], onCreateReservation
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [showHistory, setShowHistory] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const now = new Date();

  const filteredReservations = useMemo(() => {
    return reservations.filter(r => {
      const rDate = new Date(r.startDate);
      const isSameDay = rDate.getDate() === selectedDate.getDate() && 
                        rDate.getMonth() === selectedDate.getMonth() && 
                        rDate.getFullYear() === selectedDate.getFullYear();
      
      const matchesSearch = !searchQuery || r.customerName.toLowerCase().includes(searchQuery.toLowerCase()) || r.customerEmail.toLowerCase().includes(searchQuery.toLowerCase()) || (r.customerPhone?.includes(searchQuery));
      const matchesStatus = !statusFilter || r.status === statusFilter || r.completionStatus === statusFilter;
      
      return isSameDay && matchesSearch && matchesStatus;
    });
  }, [reservations, searchQuery, statusFilter, selectedDate]);

  const lateReservations = filteredReservations.filter(r => r.status === 'pending' && new Date(r.startDate) < now);
  const inProgressReservations = filteredReservations.filter(r => r.status !== 'cancelled' && r.status !== 'pending' && new Date(r.startDate) <= now && new Date(r.endDate) >= now);
  const pendingFutureReservations = filteredReservations.filter(r => r.status === 'pending' && new Date(r.startDate) > now);
  const upcomingReservations = filteredReservations.filter(r => r.status === 'confirmed' && new Date(r.startDate) > now).sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  const historyReservations = filteredReservations.filter(r => (new Date(r.endDate) < now && r.status !== 'pending') || r.status === 'cancelled');

  const recurringByPattern = useMemo(() => {
    return recurringTemplates.reduce((acc, curr) => {
      const patternKey = curr.pattern || 'other';
      if (!acc[patternKey]) acc[patternKey] = [];
      acc[patternKey].push(curr);
      return acc;
    }, {} as Record<string, any[]>);
  }, [recurringTemplates]);

  const hasActiveReservations = lateReservations.length > 0 || inProgressReservations.length > 0 || pendingFutureReservations.length > 0 || upcomingReservations.length > 0;

  const prevDay = () => { const newDate = new Date(selectedDate); newDate.setDate(newDate.getDate() - 1); setSelectedDate(newDate); };
  const nextDay = () => { const newDate = new Date(selectedDate); newDate.setDate(newDate.getDate() + 1); setSelectedDate(newDate); };
  const goToToday = () => setSelectedDate(new Date());

    return (
        <Box>
            <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 0.5, borderRadius: 4, 
                            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', 
                            display: 'flex', alignItems: 'center' 
                        }}
                    >
                        <IconButton onClick={prevDay} size="small" sx={{ borderRadius: 3 }}><ArrowBack fontSize="small" /></IconButton>
                        <Button 
                            onClick={goToToday}
                            sx={{ 
                                px: 2, fontWeight: 900, textTransform: 'none', color: isDark ? 'white' : 'primary.main',
                                fontSize: '0.9rem'
                            }}
                        >
                            {selectedDate.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </Button>
                        <IconButton onClick={nextDay} size="small" sx={{ borderRadius: 3 }}><ArrowForward fontSize="small" /></IconButton>
                    </Paper>
                    <Tooltip title="Ir a hoy">
                        <IconButton onClick={goToToday} sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}>
                            <Today fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ flex: 1, minWidth: 300, justifyContent: 'flex-end' }}>
                    <TextField 
                        size="small" 
                        placeholder="Buscar cliente..." 
                        value={searchQuery} 
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{ 
                            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                            sx: { borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'white' }
                        }} 
                        sx={{ flex: 1, maxWidth: 300 }} 
                    />

                    <ToggleButtonGroup 
                        value={viewMode} 
                        exclusive 
                        onChange={(e, value) => value && setViewMode(value)} 
                        size="small"
                        sx={{ 
                            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', 
                            p: 0.5, borderRadius: 3,
                            '& .MuiToggleButton-root': { border: 'none', borderRadius: 2.5, px: 2, '&.Mui-selected': { bgcolor: 'primary.main', color: 'white' } }
                        }}
                    >
                        <ToggleButton value="list"><ViewList fontSize="small" /></ToggleButton>
                        <ToggleButton value="timeline"><ViewTimeline fontSize="small" /></ToggleButton>
                        <ToggleButton value="kanban"><ViewKanban fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Box>

            <Box sx={{ mb: 4, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[
                    { label: 'Atrasadas', count: lateReservations.length, color: '#ef4444', icon: <Warning /> },
                    { label: 'En Curso', count: inProgressReservations.length, color: '#10b981', icon: <PlayArrow /> },
                    { label: 'Pendientes', count: pendingFutureReservations.length, color: '#f59e0b', icon: <Schedule /> },
                    { label: 'Próximas', count: upcomingReservations.length, color: '#6366f1', icon: <Event /> },
                ].map((stat, i) => (
                    <Paper 
                        key={i} 
                        elevation={0}
                        sx={{ 
                            px: 2, py: 1.5, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 1.5,
                            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'white',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                            flex: '1 1 150px'
                        }}
                    >
                        <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1 }}>{stat.count}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{stat.label}</Typography>
                        </Box>
                    </Paper>
                ))}
            </Box>

      {viewMode === 'list' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {lateReservations.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}><Warning fontSize="small" /> Atrasadas / Requieren Atención</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {lateReservations.map(reservation => <CompactReservationCard key={reservation.id} reservation={reservation} onEdit={onEdit} onDelete={onDelete} onUpdateStatus={onUpdateStatus} onOpenShiftStatus={onOpenShiftStatus} />)}
              </Box>
            </Box>
          )}
          {inProgressReservations.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}><PlayArrow fontSize="small" /> En Curso</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {inProgressReservations.map(reservation => <CompactReservationCard key={reservation.id} reservation={reservation} onEdit={onEdit} onDelete={onDelete} onUpdateStatus={onUpdateStatus} onOpenShiftStatus={onOpenShiftStatus} />)}
              </Box>
            </Box>
          )}
          {pendingFutureReservations.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}><Schedule fontSize="small" /> Pendientes por Confirmar</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {pendingFutureReservations.map(reservation => <CompactReservationCard key={reservation.id} reservation={reservation} onEdit={onEdit} onDelete={onDelete} onUpdateStatus={onUpdateStatus} onOpenShiftStatus={onOpenShiftStatus} />)}
              </Box>
            </Box>
          )}
          {upcomingReservations.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}><Event fontSize="small" /> Próximas Reservas</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {upcomingReservations.map(reservation => <CompactReservationCard key={reservation.id} reservation={reservation} onEdit={onEdit} onDelete={onDelete} onUpdateStatus={onUpdateStatus} onOpenShiftStatus={onOpenShiftStatus} />)}
              </Box>
            </Box>
          )}
          {!hasActiveReservations && (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: isDark ? 'grey.800' : 'grey.50' }}>
              <Typography variant="h6" color="text.secondary">No hay reservas para mostrar</Typography>
              <Typography variant="body2" color="text.secondary">{searchQuery || statusFilter ? 'Intenta con otros filtros' : 'Las nuevas reservas aparecerán aquí'}</Typography>
            </Paper>
          )}
        </Box>
      )}

      {viewMode === 'timeline' && <TimelineView reservations={filteredReservations} onEdit={onEdit} />}
      {viewMode === 'kanban' && <KanbanView reservations={filteredReservations} onEdit={onEdit} onUpdateStatus={onUpdateStatus} onDelete={onDelete} />}

      {recurringTemplates.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Button onClick={() => setShowHistory(!showHistory)} startIcon={showHistory ? <ExpandLess /> : <ExpandMore />} endIcon={<Repeat />} sx={{ mb: 2 }}>{showHistory ? 'Ocultar' : 'Ver'} Plantillas Recurrentes ({recurringTemplates.length})</Button>
          <Collapse in={showHistory}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {Object.entries(recurringByPattern).map(([patternKey, templates]) => {
                const templateList = templates as any[];
                const patternLabels: Record<string, string> = { daily: 'Diarias', weekly: 'Semanales', monthly: 'Mensuales', other: 'Otros' };
                return (
                  <Card key={patternKey} sx={{ minWidth: 200, flex: '1 1 250px' }}>
                    <Box sx={{ bgcolor: 'primary.light', px: 2, py: 1 }}><Typography variant="subtitle2" fontWeight="bold">{patternLabels[patternKey] || patternKey}</Typography></Box>
                    <CardContent>
                      {templateList.map((template: any) => (
                        <Box key={template.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: template.isActive ? 'success.main' : 'grey.400' }} />
                          <Typography variant="body2">{template.clientName}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{template.startTime}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Collapse>
        </Box>
      )}

      {historyReservations.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Button onClick={() => setShowHistory(!showHistory)} startIcon={showHistory ? <ExpandLess /> : <ExpandMore />} endIcon={<History />} sx={{ mb: 2 }}>{showHistory ? 'Ocultar' : 'Ver'} Historial ({historyReservations.length})</Button>
          <Collapse in={showHistory}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.7 }}>
              {historyReservations.map(reservation => <CompactReservationCard key={reservation.id} reservation={reservation} onEdit={onEdit} onDelete={onDelete} onUpdateStatus={onUpdateStatus} onOpenShiftStatus={onOpenShiftStatus} />)}
            </Box>
          </Collapse>
        </Box>
      )}

      {showTrash && deletedReservations.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.main', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><DeleteOutline /> Papelera de Reservas</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {deletedReservations.map(reservation => (
              <Paper key={reservation.id} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, opacity: 0.7, bgcolor: isDark ? 'grey.800' : 'grey.50' }}>
                <Box sx={{ flex: 1 }}><Typography variant="subtitle2">{reservation.customerName}</Typography><Typography variant="caption" color="text.secondary">{new Date(reservation.startDate).toLocaleString()}</Typography></Box>
                {onRestore && <IconButton onClick={() => onRestore(reservation.id)} color="success"><Restore /></IconButton>}
              </Paper>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReservationsSection;
